import React, { useEffect } from 'react';
import {
    CheckCircle2,
    Rocket,
    Zap,
    Film,
    Mic2,
    Pen,
    TrendingUp,
    Layers,
    Gift,
    Instagram,
    XCircle,
    Scale,
    Wand2
} from 'lucide-react';

/* ─── Reusable Primitives ─── */

const Card = ({ children, className = "", glow = false }) => (
    <div className={`rounded-2xl border ${glow
        ? 'border-indigo-500/50 bg-indigo-900/10 shadow-[0_0_50px_-15px_rgba(99,102,241,0.25)]'
        : 'border-slate-700/70 bg-slate-800/50'
        } p-6 md:p-8 backdrop-blur-sm transition-all duration-500 hover:border-white/20 ${className}`}>
        {children}
    </div>
);

const SectionTag = ({ icon: IconComp, label, color = "indigo" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        rose: "bg-rose-500/10 border-rose-500/20 text-rose-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const Check = ({ children, highlight = false }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${highlight ? 'text-indigo-400' : 'text-emerald-400'}`} />
        <span className={`leading-relaxed ${highlight ? 'text-indigo-100 font-medium' : 'text-slate-200 text-sm'}`}>{children}</span>
    </li>
);

const Cross = ({ children }) => (
    <li className="flex gap-3 items-start opacity-70">
        <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-rose-500" />
        <span className="leading-relaxed text-slate-300 text-sm">{children}</span>
    </li>
);

const StatCard = ({ value, label, sub, accent = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-3xl md:text-4xl font-black text-white mb-1">{value}</div>
            <div className="text-sm font-semibold text-white/80">{label}</div>
            {sub && <div className="text-xs text-white/50 mt-2">{sub}</div>}
        </div>
    );
};


/* ─── Main Component ─── */
export default function AIVideoProposal() {

    useEffect(() => {
        document.title = 'SocialSEO | The New Era of Animation';
    }, []);

    /* ── Package Data ── */
    const packages = [
        {
            name: "Starter",
            videos: 4,
            bonusClips: 15,
            price: "$1,450",
            perVideoLabel: "$360/video",
            color: "border-t-slate-400",
            badge: null,
            highlights: [
                "4 long-form YouTube videos (8-10 min each)",
                "Full scripting, research, and storyboarding",
                "Cinematic AI Animation & Editing",
                "Human-like AI Voiceover generation",
                "Custom sound design and background music",
                "+ All No-Brainer Bonuses Included"
            ]
        },
        {
            name: "Growth",
            videos: 8,
            bonusClips: 15,
            price: "$2,650",
            perVideoLabel: "$330/video",
            color: "border-t-indigo-500",
            badge: "Popular",
            highlights: [
                "8 long-form YouTube videos (8-10 min each)",
                "Full scripting, research, and storyboarding",
                "Cinematic AI Animation with Advanced Continuity",
                "Human-like AI Voiceover generation",
                "Custom sound design and original music generation",
                "+ All No-Brainer Bonuses Included"
            ]
        },
        {
            name: "Domination",
            videos: 12,
            bonusClips: 15,
            price: "$3,600",
            perVideoLabel: "$300/video",
            color: "border-t-purple-500",
            badge: "Best Value",
            highlights: [
                "12 long-form YouTube videos (8-10 min each)",
                "Full scripting, research, and storyboarding",
                "Premium Animation & Visual Effects",
                "Human-like AI Voiceover generation",
                "Original Soundtrack & Theme generation",
                "+ All No-Brainer Bonuses Included"
            ]
        }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span></span>
                </div>
                <div className="bg-red-500/20 backdrop-blur-lg border border-red-500/30 px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-red-300 text-xs font-bold tracking-widest uppercase">Proposal</span>
                </div>
            </header>


            <main className="max-w-[1120px] mx-auto px-4 space-y-28">

                {/* ═══════ HERO ═══════ */}
                <section className="text-center pt-10">
                    <SectionTag icon={Rocket} label="Welcome to the New Era of Animation" />
                    <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight pb-2">
                        Studio Quality Animation.<br />100% AI-Native.
                    </h1>
                    <p className="text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-12">
                        Traditional 3D animation takes months, massive teams, and huge budgets. We've built an end-to-end proprietary infrastructure to deliver cinematic, high-retention content at scale. You bring the vision. We build the machine.
                    </p>

                    {/* Hero Stats */}
                    <div className="grid grid-cols-2 max-w-xl mx-auto gap-4 mt-8">
                        <StatCard value="10X" label="Faster & Scalable" sub="End-to-End Production" accent="emerald" />
                        <StatCard value="8-10 min" label="Long-Form" sub="Cinematic Quality" accent="indigo" />
                    </div>
                </section>


                {/* ═══════ THE COST SHOCK COMPARISON ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Scale} label="The Reality Check" color="rose" />
                        <h2 className="text-4xl font-black mb-4">Why the Old Model is Dead</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto mb-8">
                            Here is exactly what it costs to produce a standard package of <strong>FOUR (8-10 minute) animated videos</strong> in 2026.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6 max-w-[1100px] mx-auto">
                        
                        {/* Option 1: Top Tier Studio */}
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-800/30 p-8 flex flex-col relative opacity-80 scale-95">
                            <h3 className="text-xl font-bold mb-2 text-slate-300">1. Top-Tier Studio<br/>(Traditional 3D)</h3>
                            <div className="text-3xl font-black text-rose-400 my-4">$72,000+</div>
                            <div className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-6">For 4 Monthly Videos</div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Cross>$2,400+ per minute of 3D animation.</Cross>
                                <Cross>Takes 6 to 10 weeks to produce.</Cross>
                                <Cross>Requires a team of 15+ riggers, modellers, and animators.</Cross>
                                <Cross>Rigid revision pipeline.</Cross>
                            </ul>
                            <div className="mt-auto py-3 bg-rose-500/10 rounded-lg text-center border border-rose-500/20 text-rose-400 font-bold text-sm">
                                Too slow. Too expensive.
                            </div>
                        </div>

                        {/* Option 2: Freelance Team */}
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-800/30 p-8 flex flex-col relative opacity-80 scale-95">
                            <h3 className="text-xl font-bold mb-2 text-slate-300">2. Hiring a Freelance<br/>Animation Team</h3>
                            <div className="text-3xl font-black text-amber-400 my-4">$19,000+</div>
                            <div className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-6">For 4 Monthly Videos</div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Cross>$600+ per minute of 2D/3D animation.</Cross>
                                <Cross>You have to manage writers, animators, and sound designers yourself.</Cross>
                                <Cross>Inconsistent quality and missed deadlines.</Cross>
                                <Cross>Still takes 3 to 4 weeks per video.</Cross>
                            </ul>
                            <div className="mt-auto py-3 bg-amber-500/10 rounded-lg text-center border border-amber-500/20 text-amber-400 font-bold text-sm">
                                High management overhead.
                            </div>
                        </div>

                        {/* Option 3: SocialSEO AI Engine */}
                        <div className="rounded-2xl border border-emerald-500/50 bg-emerald-900/10 p-8 flex flex-col relative shadow-[0_0_40px_-10px_rgba(16,185,129,0.3)] z-10 scale-105 transform">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-slate-900 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg">
                                The AI Advantage
                            </div>
                            <h3 className="text-xl font-bold mb-2 text-white mt-2">3. SocialSEO AI<br/>Production Engine</h3>
                            <div className="text-4xl font-black text-emerald-400 my-4">$1,450</div>
                            <div className="text-sm font-semibold text-emerald-400/80 uppercase tracking-widest mb-6">For 4 Monthly Videos</div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Check>Cinematic AI video generation</Check>
                                <Check>Delivered at massive speed.</Check>
                                <Check>We handle everything: Scripting, Voice, Music, VFX, and SEO.</Check>
                                <Check>Infinitely scalable. Produce up to 12+ videos a month.</Check>
                            </ul>
                            <div className="mt-auto py-3 bg-emerald-50 text-slate-900 rounded-lg text-center font-bold text-sm shadow-md">
                                Unbeatable ROI.
                            </div>
                        </div>

                    </div>
                </section>


                {/* ═══════ DETAILED DELIVERABLES ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Layers} label="What You Actually Get" />
                        <h2 className="text-4xl font-black mb-4">The Detailed Deliverables</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Every video we produce goes through a rigorous pipeline to ensure it is YouTube-ready, retention-optimized, and cinematic.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-5xl mx-auto bg-slate-900/40 p-8 md:p-12 rounded-3xl border border-white/5">
                        
                        <div>
                            <div className="flex items-center gap-3 mb-4 text-indigo-300">
                                <Pen className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">Pre-Production & Scripting</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Deep Niche Research:</strong> Trend analysis and competitor review.</Check>
                                <Check><strong>High-Retention Scripting:</strong> Hook writing and pacing designed specifically for the YouTube algorithm.</Check>
                                <Check><strong>Storyboard Approval:</strong> You review the script and structure before we generate a single frame.</Check>
                            </ul>
                        </div>

                        <div>
                            <div className="flex items-center gap-3 mb-4 text-purple-300">
                                <Wand2 className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">Cinematic Visuals</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Start-Image Pinning:</strong> Ensures visual continuity and character consistency across the entire video.</Check>
                                <Check><strong>Dynamic Generation:</strong> We curate hundreds of custom micro-scenes to build the narrative effortlessly.</Check>
                            </ul>
                        </div>

                        <div>
                            <div className="flex items-center gap-3 mb-4 text-emerald-300">
                                <Mic2 className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">Audio & Sound Design</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Studio-Grade Voiceover:</strong> AI voice models that sound completely human, with perfect pacing and emotion.</Check>
                                <Check><strong>Custom Soundtrack:</strong> Mood-matched music generated exactly for your video scenes.</Check>
                                <Check><strong>SFX Layering:</strong> Sound effects to build tension and retain viewer attention.</Check>
                            </ul>
                        </div>

                        <div>
                            <div className="flex items-center gap-3 mb-4 text-amber-300">
                                <Film className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">Post-Production & Assembly</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Seamless Editing:</strong> Stitching clips, color grading, and adding transitions to match the narrative.</Check>
                                <Check><strong>Text & VFX Overlays:</strong> Pop-ups and dynamic elements to keep the visual pacing fast.</Check>
                                <Check><strong>Quality Assurance:</strong> Final human review to ensure it looks and feels like a studio production.</Check>
                            </ul>
                        </div>

                    </div>
                </section>


                {/* ═══════ BONUSES ═══════ */}
                <section>
                    <div className="max-w-4xl mx-auto relative">
                        {/* Background glow */}
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-indigo-500/20 blur-3xl rounded-full" />
                        
                        <div className="relative bg-slate-900 border border-indigo-500/30 rounded-3xl p-8 md:p-12 shadow-2xl">
                            <div className="absolute top-0 right-10 translate-y-[-50%] bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-bold px-6 py-2 rounded-full flex items-center gap-2 shadow-lg">
                                <Gift className="w-5 h-5" /> The "No-Brainer" Stack
                            </div>
                            
                            <h2 className="text-3xl font-black mb-8 text-center mt-4">Included Free With Every Plan</h2>
                            
                            <div className="space-y-6">
                                <div className="flex gap-4 p-5 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors">
                                    <div className="bg-pink-500/20 p-3 rounded-xl h-fit text-pink-400">
                                        <Instagram className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-start mb-1">
                                            <h4 className="text-lg font-bold">15+ Instagram Reels & Shorts</h4>
                                        </div>
                                        <p className="text-slate-400 text-sm">We manually extract the best, most viral hooks from your long-form YouTube videos, reformat them to 9:16 vertical, add premium captions, and deliver them to you ready for Instagram, Shorts, and TikTok. (Need more? We can negotiate up to 30 clips easily). </p>
                                    </div>
                                </div>

                                <div className="flex gap-4 p-5 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors">
                                    <div className="bg-emerald-500/20 p-3 rounded-xl h-fit text-emerald-400">
                                        <TrendingUp className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-start mb-1">
                                            <h4 className="text-lg font-bold">YouTube SEO & Management</h4>
                                        </div>
                                        <p className="text-slate-400 text-sm">We don't just hand you a video file. We provide custom YouTube thumbnails, SEO-optimized titles, algorithmic descriptions, and tag sets. We completely handle the uploads and schedule it directly to your channel.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>


                {/* ═══════ PRICING PACKAGES ═══════ */}
                <section id="plans">
                    <div className="text-center mb-10">
                        <SectionTag icon={Zap} label="Investment" />
                        <h2 className="text-4xl font-black mb-4">Choose Your Growth Engine</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto mb-8">
                            Lock in your production volume. All packages include end-to-end production + all bonuses.
                        </p>
                    </div>

                    {/* Package Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1100px] mx-auto">
                        {packages.map((pkg, idx) => (
                            <div
                                key={idx}
                                className={`relative rounded-2xl border-t-4 ${pkg.color} border border-slate-700/70 bg-slate-800/50 backdrop-blur-sm p-6 md:p-8 flex flex-col transition-all duration-500 hover:border-white/20 hover:translate-y-[-4px]`}
                            >
                                {pkg.badge && (
                                    <div className="absolute -top-3 right-4 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                        {pkg.badge}
                                    </div>
                                )}

                                <div className="text-center pb-6 border-b border-white/10 mb-6">
                                    <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                                    <p className="text-slate-400 text-sm">
                                        {pkg.videos} YouTube Videos / month
                                    </p>
                                </div>

                                <div className="text-center mb-2">
                                    <div className="text-4xl font-black text-white">{pkg.price}</div>
                                    <div className="text-sm text-white/40 mt-1">/ month</div>
                                </div>
                                <div className="text-center mb-6">
                                    <span className="text-xs text-indigo-300 font-semibold bg-indigo-500/10 px-3 py-1 rounded-full">{pkg.perVideoLabel}</span>
                                </div>

                                <ul className="space-y-3 flex-grow mb-6">
                                    {pkg.highlights.map((h, i) => (
                                        <Check key={i}>{h}</Check>
                                    ))}
                                </ul>

                                {/* Quarterly Discount */}
                                <div className="mt-auto bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                                    <p className="text-xs text-white/60 mb-1 uppercase tracking-wide">3-Month Commitment</p>
                                    <p className="text-lg font-bold text-emerald-400">20% OFF</p>
                                    <p className="text-xs text-white/50 mt-1">Lock in the rate. Save big.</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>


                {/* ═══════ FOOTER CTA ═══════ */}
                <section className="text-center pb-10">
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-indigo-900/50 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">
                        <Rocket className="w-10 h-10 text-indigo-400 mx-auto mb-4" />
                        <h2 className="text-3xl md:text-4xl font-black mb-4 text-white">Ready to Scale?</h2>
                        <p className="text-slate-300 text-lg mb-2">
                            The smartest marketing investment you will make this year.
                        </p>
                        <p className="text-indigo-300 text-sm font-semibold uppercase tracking-widest mt-6">
                            Reply to this message on WhatsApp to get started
                        </p>
                    </div>
                </section>

            </main>
        </div>
    );
}
